/**
 * Samita Wholesale Price Fix
 * ------------------------------------------------------------------------------
 * Fixes:
 * 1. Product Cards & Search Results: Tax-exempt 'Compare At' prices.
 * 2. Single Product Page (PDP): Tax-exempt 'Compare At' prices.
 * 3. Cart/Mini-Cart: Tax-exempt 'Total Compare' prices and 'Amount Saved'.
 * 4. Cart Injection: Injects missing compare prices for products where Samita hides them.
 *
 * v4: Added injection logic for missing Cart compare prices.
 */

(function () {
  "use strict";

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log("[SamitaFix]", ...args);

  // Configuration
  const SELECTORS = {
    // Product Cards / Search Results
    cardPriceContainer: ".samitaWS-customized-tax",
    cardComparePrice: ".price--compare", // Standard theme selector

    // Single Product Page (PDP) & Search Grid specific inclusions
    pdpComparePrice: ".samitaWS-customized-original-price",
    pdpNetPrice: ".samitaWS-exclude-price",

    // Product ID Attributes
    productIdAttr: "samitaws-product-id",
    variantIdAttr: "samitaws-variant-id",

    // Cart / Mini-Cart
    cartItem: [".cart-item", ".line-item", ".mini-cart__line-item"],
    amountSaved: [".mini-cart__amount-saved", ".cart-recap__amount-saved"],
    totalComparePrice: [
      ".mini-cart__compare-at-price",
      ".cart-recap__total-compare-at-price",
      ".samitaWS-cart-total-original-price",
    ],
  };

  let observer = null;
  const observerConfig = { childList: true, subtree: true, characterData: true };

  /**
   * Helper: Format number to currency string
   */
  const formatValueInString = (originalString, newValue) => {
    // Never emit "NaN": if the value isn't a finite number, leave the string untouched.
    if (!Number.isFinite(newValue)) return originalString;
    const formattedNumber = newValue.toFixed(2);
    // If the string currently shows "NaN" (no numeric token), replace that token directly.
    if (/NaN/i.test(originalString) && !/[\d]/.test(originalString)) {
      return originalString.replace(/NaN/i, formattedNumber);
    }
    // Try to find an existing number pattern to respect currency symbol placement
    const numberPattern = /[\d’'.,]+/;
    const match = originalString.match(numberPattern);

    if (match) {
      const originalNumberStr = match[0];
      let newNumberStr = formattedNumber;
      // Basic localization check (comma vs dot)
      if (originalNumberStr.includes(",") && !originalNumberStr.includes(".")) {
        newNumberStr = newNumberStr.replace(".", ",");
      }
      if (originalNumberStr === newNumberStr) return originalString;
      return originalString.replace(originalNumberStr, newNumberStr);
    }
    // Fallback if no specific pattern found
    return originalString.replace(/[\d\.,]+/, formattedNumber);
  };

  const getTaxHandle = () => {
    return window.Samita && window.Samita.Wholesale && window.Samita.Wholesale.TaxDisplayHandle
      ? window.Samita.Wholesale.TaxDisplayHandle
      : null;
  };

  /**
   * Safe wrapper around Samita's TaxDisplayHandle.excludeTax.
   *
   * IMPORTANT: Samita's real signature is `excludeTax(product, productForCollections, amount)`.
   * The amount to convert is the THIRD argument – the second argument is the product again
   * (used internally to read `product.collections` for tax overrides).
   *
   * Calling it with only two arguments (the previous behaviour) made `amount` land in the
   * `productForCollections` slot, so the real amount was `undefined`. The function then
   * returned `undefined`, which turned every downstream price into "NaN" on the product page.
   *
   * Returns the NET (tax-excluded) value in the SAME unit as the input `amount`, or `null`
   * when it cannot be computed (so callers can skip instead of rendering "NaN").
   *
   * NOTE: Samita only strips VAT when the passed product carries
   * `configs.general.price_include_tax`. The objects in `Samita.Wholesale.products` don't
   * include that flag, so `excludeTax` would otherwise return the gross price unchanged.
   * The store's Shopify prices are gross (VAT included, Swiss convention) and the applicable
   * rate is resolved by Samita via its tax-display collection overrides (e.g. the 2.6% food
   * rate), so we enrich a shallow copy with the flag while preserving the product's own config.
   */
  const excludeTaxSafe = (product, amount) => {
    const TaxHandle = getTaxHandle();
    if (!TaxHandle || product == null || amount == null) return null;
    const prod = Object.assign({}, product);
    prod.configs = Object.assign({}, product.configs);
    prod.configs.general = Object.assign({ price_include_tax: true }, product.configs && product.configs.general);
    const result = TaxHandle.excludeTax(prod, prod, amount);
    const num = typeof result === "number" ? result : parseFloat(result);
    return Number.isFinite(num) ? num : null;
  };

  /**
   * Returns true only if the current customer is a B2B wholesale customer.
   * Samita may load for ALL visitors – we must not modify prices for regular customers.
   */
  const isB2BCustomer = () => {
    if (!window.Samita || !window.Samita.Wholesale) return false;
    const sw = window.Samita.Wholesale;

    // Not logged in at all
    if (!sw.customer) return false;

    const customerTags = (sw.customer.tags || []).map((t) => t.toLowerCase());

    // 1. Check against Samita's own wholesalePricing config tags
    if (sw.wholesalePricing) {
      const configTags = sw.wholesalePricing
        .flatMap((p) =>
          p?.customer_configs?.apply_customer?.tags
            ? p.customer_configs.apply_customer.tags
            : []
        )
        .map((t) => t.toLowerCase());
      if (customerTags.some((t) => configTags.includes(t))) return true;
    }

    // 2. Fallback: known B2B tags used in this store
    const knownB2BTags = ["b2b", "reseller", "wholesale", "b2b-kleinkunden", "b2b-test"];
    return customerTags.some((t) => knownB2BTags.includes(t));
  };

  /**
   * Fix Single Element (Card or PDP)
   */
  const fixPriceElement = (compareEl, contextEl) => {
    // 1. Resolve Product ID
    let productId = contextEl.getAttribute("data-product-id");
    const samitaEl = contextEl.querySelector(`[${SELECTORS.productIdAttr}]`);
    if (samitaEl) productId = samitaEl.getAttribute(SELECTORS.productIdAttr);

    // Fallback: Check if contextEl itself has the attribute
    if (!productId && contextEl.hasAttribute(SELECTORS.productIdAttr)) {
      productId = contextEl.getAttribute(SELECTORS.productIdAttr);
    }

    let variantId = samitaEl ? samitaEl.getAttribute(SELECTORS.variantIdAttr) : null;
    if (!variantId && contextEl.hasAttribute(SELECTORS.variantIdAttr)) {
      variantId = contextEl.getAttribute(SELECTORS.variantIdAttr);
    }

    // --- PDP Fallback Logic ---
    // If we are on a PDP and the price element was refreshed (stripping attributes),
    // we need to find the IDs from the main product form/context.
    if (
      (!productId || !variantId) &&
      (contextEl.classList.contains("product-form") ||
        contextEl.classList.contains("product-meta") ||
        contextEl.closest(".product-form"))
    ) {
      // Attempt to find the main product ID from the data-product-json script
      if (!productId) {
        const productJsonScript = document.querySelector("[data-product-json]");
        if (productJsonScript) {
          try {
            const productData = JSON.parse(productJsonScript.innerHTML);
            if (productData && productData.product) {
              productId = productData.product.id;
            }
          } catch (e) {
            console.error("SamitaFix: Failed to parse product JSON", e);
          }
        }
      }

      // Attempt to find the current variant ID from the form input
      if (!variantId) {
        // Try to find the input with name="id" inside the product form
        const formObj = contextEl.closest("form") || document.querySelector('form[action*="/cart/add"]');
        if (formObj) {
          const variantInput = formObj.querySelector('[name="id"]');
          if (variantInput) {
            variantId = variantInput.value;
          }
        }
        // Fallback: URL param
        if (!variantId) {
          const urlParams = new URLSearchParams(window.location.search);
          variantId = urlParams.get("variant");
        }
      }
    }

    // --- Cart Fallback Logic ---
    if (!variantId) {
      // If we are in a cart item, try to get the variant ID from the row
      const cartItem = contextEl.closest(".cart-item") || contextEl.closest(".line-item");
      if (cartItem) {
        variantId = cartItem.getAttribute("data-variant-id");
        if (!variantId && cartItem.getAttribute("data-key")) {
          variantId = cartItem.getAttribute("data-key").split(":")[0];
        }
      }
    }
    // ---------------------------
    // --------------------------

    if (!productId) return;

    if (!window.Samita || !window.Samita.Wholesale || !window.Samita.Wholesale.products) return;

    const product = window.Samita.Wholesale.products.find((p) => p.id == productId);
    if (!product) return;

    let currentVariant = null;
    if (variantId && product.variants) {
      currentVariant = product.variants.find((v) => v.id == variantId);
    }

    // If we can't find the specific variant, we cannot safely display a compare-at price.
    // Defaulting to the first variant causes issues when the first variant is on sale but the selected one isn't.
    if (variantId && !currentVariant) return;

    // Use product data if no variant ID is present (e.g. simple product)
    const referenceData = currentVariant || product;
    const compareAtPrice = referenceData.compare_at_price;
    const price = referenceData.price;

    // If no compare_at_price is defined in Samita data, it means there is no discount for this B2B customer.
    // We must ensure the compare element is hidden, as the external plugin might have displayed it erroneously.
    if (!compareAtPrice) {
      compareEl.style.setProperty("display", "none", "important");
      compareEl.setAttribute("data-samita-fixed", "true");
      return;
    }

    const TaxHandle = getTaxHandle();
    if (!TaxHandle) return;

    // Calculate Net Prices (compareAtPrice/price are in cents → result is in cents).
    const netCompare = excludeTaxSafe(product, compareAtPrice);
    const netPrice = excludeTaxSafe(product, price);
    // If tax exclusion couldn't be computed, don't risk writing "NaN" – leave Samita's render.
    if (netCompare === null || netPrice === null) return;

    const correctValue = netCompare / 100;
    const correctPriceValue = netPrice / 100;

    // Check if compare price equals regular price
    // We use a small epsilon for float comparison safety, though exact match usually works here
    if (Math.abs(correctValue - correctPriceValue) < 0.01) {
      compareEl.style.setProperty("display", "none", "important");
      compareEl.setAttribute("data-samita-fixed", "true");
      return;
    } else {
      compareEl.style.display = ""; // Reset display if it was hidden
    }

    // Apply Update
    const currentText = compareEl.textContent;
    const newText = formatValueInString(currentText, correctValue);

    // Safety Check
    if (currentText === newText) {
      if (!compareEl.hasAttribute("data-samita-fixed")) {
        compareEl.setAttribute("data-samita-fixed", "true");
      }
      return;
    }

    if (currentText !== newText) {
      findAndReplaceNumberInNode(compareEl, correctValue);
      compareEl.setAttribute("data-samita-fixed", "true");
    }
  };

  /**
   * Inject Missing Compare Prices in Cart
   * Fixes items like 'Le Parfait Thon' where Samita renders only the net price.
   */
  const injectMissingCartComparePrices = () => {
    const cartItems = document.querySelectorAll(SELECTORS.cartItem.join(","));
    if (cartItems.length === 0) return;

    const TaxHandle = getTaxHandle();
    if (!TaxHandle || !window.Samita.Wholesale.products) return;

    cartItems.forEach((item) => {
      // Identify Product
      const samitaEl = item.querySelector(`[${SELECTORS.productIdAttr}]`);
      if (!samitaEl) return;

      const productId = samitaEl.getAttribute(SELECTORS.productIdAttr);
      const variantId = samitaEl.getAttribute(SELECTORS.variantIdAttr);

      if (!productId) return;

      // Fallback: If variantId is missing, check the cart item row
      if (!variantId) {
        const cartItemRow = item.closest(".cart-item") || item.closest(".line-item");
        if (cartItemRow) {
          variantId = cartItemRow.getAttribute("data-variant-id");
          if (!variantId && cartItemRow.getAttribute("data-key")) {
            // sometimes key is "variant_id:properties"
            variantId = cartItemRow.getAttribute("data-key").split(":")[0];
          }
        }
      }

      // Check if compare price is already present
      if (item.querySelector(SELECTORS.pdpComparePrice) || item.querySelector(".price--compare")) {
        // Exists, likely fixed by fixPriceElement or already correct.
        return;
      }

      // Data Lookup
      const product = window.Samita.Wholesale.products.find((p) => p.id == productId);
      if (!product) return;

      let compareBase = null;
      let priceBase = null;

      let v = null;
      if (variantId && product.variants) {
        v = product.variants.find((varObj) => varObj.id == variantId);
      }

      if (v) {
        compareBase = v.compare_at_price;
        priceBase = v.price;
      } else {
        compareBase = product.compare_at_price;
        priceBase = product.price;
      }

      if (!compareBase) return; // No compare price in data, nothing to inject

      // Calculate Net Values (compareBase/priceBase are in cents → result is in cents).
      const netCompareRaw = excludeTaxSafe(product, compareBase);
      const netPriceRaw = excludeTaxSafe(product, priceBase);
      // Bail out if tax exclusion couldn't be computed – never inject a "NaN" price.
      if (netCompareRaw === null || netPriceRaw === null) return;

      const netCompareValue = netCompareRaw / 100;
      const netPriceValue = netPriceRaw / 100;

      // Skip if prices are effectively equal
      if (Math.abs(netCompareValue - netPriceValue) < 0.01) return;

      // Find injection point (usually inside the price list or before the current price)
      const priceList =
        item.querySelector(".line-item__price-list") ||
        item.querySelector(".cart-item__price-list") ||
        item.querySelector(".product-item__price-list") ||
        item.querySelector(".mini-cart__price-list");
      if (!priceList) return;

      // Create Element
      const wrapper = document.createElement("span");
      wrapper.className = "samitaWS-customized-tax";
      // Note: We use the same class structure to match existing styles

      const compareSpan = document.createElement("span");
      compareSpan.className = "samitaWS-customized-original-price";
      compareSpan.textContent = `CHF ${netCompareValue.toFixed(2)}`; // Initial formatting
      compareSpan.setAttribute("data-samita-fixed", "true");
      compareSpan.style.textDecoration = "line-through";
      compareSpan.style.marginRight = "5px";
      compareSpan.style.opacity = "0.7";

      wrapper.appendChild(compareSpan);

      // Append to price list (so it appears after the current price)
      priceList.appendChild(wrapper);

      // Mark as handled to avoid double injection
      samitaEl.setAttribute("data-samita-injected", "true");
    });
  };

  /**
   * Fix Cart Totals
   */
  const fixCartTotals = () => {
    if (!window.Samita || !window.Samita.Wholesale || !window.Samita.Wholesale.cart) return;

    const cart = window.Samita.Wholesale.cart;
    const products = window.Samita.Wholesale.products;
    const TaxHandle = getTaxHandle();
    if (!TaxHandle || !products) return;

    let totalNetOriginal = 0;
    let totalNetCurrent = 0;

    cart.items.forEach((item) => {
      const product = products.find((p) => p.id == item.product_id);
      if (!product) {
        totalNetCurrent += item.price;
        totalNetOriginal += item.price;
        return;
      }
      const netItemPrice = excludeTaxSafe(product, item.price);
      if (netItemPrice === null) {
        // Cannot compute – fall back to the raw price so totals never become NaN.
        totalNetCurrent += item.price * item.quantity;
        totalNetOriginal += item.price * item.quantity;
        return;
      }
      totalNetCurrent += netItemPrice * item.quantity;

      let compareBase = null;

      let v = null;
      if (product.variants) {
        v = product.variants.find((varObj) => varObj.id == item.variant_id);
      }

      if (v) {
        compareBase = v.compare_at_price;
      } else {
        compareBase = product.compare_at_price || item.original_price || item.price;
      }

      // Fallback if compareBase is still null/undefined, though the logic above handles the main split.
      // Ideally if v exists and v.compare_at_price is null, compareBase is null (no savings).

      // If no compareBase, we shouldn't add to totalNetOriginal more than the current price (or treat as 0 diff)
      if (!compareBase) {
        compareBase = item.price; // Or item.original_price, treating as no discount
      }
      const netItemCompare = excludeTaxSafe(product, compareBase);
      // Fall back to the raw compare base if tax exclusion cannot be computed.
      totalNetOriginal += (netItemCompare === null ? compareBase : netItemCompare) * item.quantity;
    });

    const netSaved = totalNetOriginal - totalNetCurrent;
    const valTotalOriginal = totalNetOriginal / 100;
    const valSaved = netSaved / 100;

    // Fix "Saved Amount"
    SELECTORS.amountSaved.forEach((sel) => {
      const els = document.querySelectorAll(sel);
      els.forEach((el) => {
        if (valSaved > 0) {
          findAndReplaceNumberInNode(el, valSaved);
          el.style.visibility = "visible";
        }
      });
    });

    // Fix "Total Compare Price"
    SELECTORS.totalComparePrice.forEach((sel) => {
      const els = document.querySelectorAll(sel);
      els.forEach((el) => {
        if (valTotalOriginal > 0 && valTotalOriginal > totalNetCurrent / 100) {
          findAndReplaceNumberInNode(el, valTotalOriginal);
        }
      });
    });
  };

  const findAndReplaceNumberInNode = (element, newValue) => {
    if (element.children.length === 0) {
      const oldTxt = element.textContent;
      const newTxt = formatValueInString(oldTxt, newValue);
      if (oldTxt !== newTxt) {
        element.textContent = newTxt;
      }
      return;
    }
    element.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        if (/[\d]/.test(node.nodeValue)) {
          const oldNum = node.nodeValue;
          const newNum = formatValueInString(oldNum, newValue);
          if (oldNum !== newNum) {
            node.nodeValue = newNum;
          }
        }
      } else {
        findAndReplaceNumberInNode(node, newValue);
      }
    });
  };

  /**
   * Cleanup Compare Prices (B2B Fix)
   * Ensures that inside .price--compare, we only show the original price and NOT the current price.
   */
  const cleanupComparePrices = () => {
    const compareContainers = document.querySelectorAll(SELECTORS.cardComparePrice);
    compareContainers.forEach((container) => {
      // 1. Hide the unwanted "current price" that Samita might inject here
      const unwantedPrices = container.querySelectorAll(".samitaWS-customized-price");
      unwantedPrices.forEach((el) => {
        el.style.setProperty("display", "none", "important");
      });

      // 2. Ensure the "original price" wrapper is visible
      const originalPrices = container.querySelectorAll(".samitaWS-customized-original-price");
      originalPrices.forEach((el) => {
        // Remove any inline display:none that might have been added
        el.style.removeProperty("display");
        el.style.setProperty("display", "inline-block", "important");

        // Force visibility if needed, but usually removing 'display' is safer to let CSS handle it.
        // If it's still hidden, we might need:
        // el.style.setProperty('display', 'inline-block', 'important');
      });

      // Edge Case: If Samita wrapped everything in .samitaWS-customized-tax but NO .samitaWS-customized-original-price exists,
      // we shouldn't hide .samitaWS-customized-price blindly?
      // Actually, per user request, .samitaWS-customized-price specifically is wrong in the compare area.
    });
  };

  /**
   * Fix PDP Main Price (B2B Sale-Price Conflict)
   * When a product has BOTH a Shopify Angebotspreis (sale) AND a B2B price,
   * Samita's initial render incorrectly uses the Shopify sale price instead
   * of the B2B finalPrice. This function reads the correct finalPrice from
   * Samita's data and patches the displayed main price on the PDP.
   */
  const fixPdpMainPrice = () => {
    // Only run on PDP (look for the product-form price-list)
    const priceList = document.querySelector(".product-form .price-list, .product-meta .price-list");
    if (!priceList) return;

    if (!window.Samita?.Wholesale?.products || !window.Samita?.Wholesale?.TaxDisplayHandle) return;

    // Find current product ID + variant ID from the embedded JSON
    const productJsonScript = document.querySelector("[data-product-json]");
    if (!productJsonScript) return;

    let productId, variantId;
    try {
      const data = JSON.parse(productJsonScript.innerHTML);
      productId = data.product?.id;
      variantId = data.selected_variant_id;
    } catch (e) {
      return;
    }

    // Fallback: try variant from form input or URL
    if (!variantId) {
      const formObj = document.querySelector('form[action*="/cart/add"]');
      if (formObj) {
        const variantInput = formObj.querySelector('[name="id"]');
        if (variantInput) variantId = variantInput.value;
      }
      if (!variantId) {
        variantId = new URLSearchParams(window.location.search).get("variant");
      }
    }

    const product = window.Samita.Wholesale.products.find((p) => p.id == productId);
    if (!product) return;

    let variant = null;
    if (variantId && product.variants) {
      variant = product.variants.find((v) => v.id == variantId);
    }

    const ref = variant || product;
    // finalPrice is the B2B-discounted price that Samita calculates (decimal, e.g. 27.19).
    // IMPORTANT: Only proceed if finalPrice actually exists. If it doesn't, this product
    // has no B2B discount and doesn't need fixing. We must NOT fall back to ref.price
    // because that value is in CENTS (e.g. 3600) while excludeTax expects decimal input.
    const finalPrice = ref.finalPrice;
    if (!finalPrice) return;

    // Note: finalPrice is already in decimal (e.g. 27.19), and excludeTax
    // returns decimal too (e.g. 26.50). No division by 100 needed.
    const correctNetPrice = excludeTaxSafe(product, finalPrice);
    // If tax exclusion couldn't be computed, don't touch the price (avoid "NaN").
    if (correctNetPrice === null) return;

    // Find the main price element (NOT the compare/crossed-out price).
    // In the Samita DOM, the main price is a single <span> with BOTH
    // .price--highlight and .samitaWS-exclude-price as classes on the SAME element
    // (compound selector, no space). We must avoid matching the .samitaWS-exclude-price
    // that lives INSIDE .price--compare (which is the crossed-out compare price).
    const mainPriceEl =
      priceList.querySelector(".price--highlight.samitaWS-exclude-price") ||
      priceList.querySelector(".price--highlight.samitaWS-customized-price") ||
      priceList.querySelector(".price.samitaWS-customized-price:not(.price--compare)");

    if (!mainPriceEl) return;

    // Parse the VISIBLE displayed value only.
    // The element may contain a .visually-hidden child with the B2B price label
    // (e.g. "B26.50B Price"), which would fool textContent into thinking the
    // price is already correct. We read only direct text nodes to get "CHF 35.09".
    let visibleText = "";
    mainPriceEl.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        // Text node — always visible
        visibleText += node.nodeValue;
      } else if (node.nodeType === 1 && !node.classList.contains("visually-hidden")) {
        visibleText += node.textContent;
      }
    });

    const currentMatch = visibleText.match(/[\d'',.]+/);

    // Defensive: if the displayed price is literally "NaN" (no numeric token, e.g. an
    // earlier render step failed), overwrite that "NaN" with the correct B2B net price.
    if (!currentMatch) {
      if (/NaN/i.test(visibleText)) {
        const formatted = correctNetPrice.toFixed(2);
        mainPriceEl.childNodes.forEach((node) => {
          if (node.nodeType === 3 && /NaN/i.test(node.nodeValue)) {
            node.nodeValue = node.nodeValue.replace(/NaN/i, formatted);
          }
        });
        mainPriceEl.setAttribute("data-samita-main-fixed", "true");
      }
      return;
    }

    const currentValue = parseFloat(currentMatch[0].replace(/[''']/g, "").replace(",", "."));

    // If the displayed price doesn't match the expected B2B net price, fix it
    if (Math.abs(currentValue - correctNetPrice) > 0.01) {
      log("PDP main price mismatch:", currentValue, "→", correctNetPrice);
      // Replace only the visible text node containing the price number
      mainPriceEl.childNodes.forEach((node) => {
        if (node.nodeType === 3 && /[\d]/.test(node.nodeValue)) {
          const formatted = correctNetPrice.toFixed(2);
          // Respect locale (comma vs dot)
          let newNum = formatted;
          if (node.nodeValue.includes(",") && !node.nodeValue.includes(".")) {
            newNum = newNum.replace(".", ",");
          }
          node.nodeValue = node.nodeValue.replace(/[\d''.,]+/, newNum);
        }
      });
      mainPriceEl.setAttribute("data-samita-main-fixed", "true");
    }
  };

  /**
   * Scan And Apply Fixes
   */
  const scanAndFixAll = () => {
    // Only run price fixes for B2B customers – Samita may load for all visitors
    if (!isB2BCustomer()) {
      if (observer) observer.observe(document.body, observerConfig);
      return;
    }

    // Pause Observer
    if (observer) observer.disconnect();

    try {
      // 1. Fix Standard Product Cards (Collections)
      const cardElements = document.querySelectorAll(SELECTORS.cardComparePrice);
      cardElements.forEach((compareEl) => {
        const contextEl =
          compareEl.closest(".product-item") ||
          compareEl.closest(".product-single") ||
          compareEl.closest(".product-form") ||
          compareEl.closest(".product-meta") ||
          compareEl.closest(SELECTORS.cardPriceContainer);
        if (contextEl) fixPriceElement(compareEl, contextEl);
      });

      // 2. Fix Single Product Page / Search Results (using new specific selector)
      const pdpElements = document.querySelectorAll(SELECTORS.pdpComparePrice);
      pdpElements.forEach((compareEl) => {
        // Context is usually the wrapper '.samitaWS-customized-tax' or similar
        const contextEl = compareEl.closest(SELECTORS.cardPriceContainer) || compareEl.parentElement;
        if (contextEl) fixPriceElement(compareEl, contextEl);
      });

      // 3. Inject Missing Compare Prices in Cart
      injectMissingCartComparePrices();

      // 4. Fix Cart Totals
      fixCartTotals();

      // 5. Fix PDP Main Price (B2B vs Shopify Sale conflict)
      fixPdpMainPrice();

      // 6. Cleanup B2B Double Prices in Compare Area
      cleanupComparePrices();
    } catch (e) {
      console.error("SamitaFix Error:", e);
    } finally {
      // Resume Observer
      if (observer) observer.observe(document.body, observerConfig);
    }
  };

  /**
   * Observer Logic
   */
  const processMutations = (mutations) => {
    scanAndFixAll();
  };

  // Start Initialization
  const init = () => {
    log("Initializing Samita Fix Observer (v4)...");

    observer = new MutationObserver(processMutations);

    scanAndFixAll(); // Initial Run

    // Polling backup
    setInterval(scanAndFixAll, 2000);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
